import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { registerPushSubscription } from '../../components/NotificationBell';
import { Copy, RefreshCw, Shield, Terminal, Key, FileText, Check } from 'lucide-react';

const CatracaAPIPage = () => {
  const [apiKey, setApiKey] = useState(null);
  const [gymId, setGymId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [keyRes, logsRes] = await Promise.all([
        axios.get(`${API}/gyms/catraca-key`),
        axios.get(`${API}/gyms/catraca-logs?limit=20`).catch(() => ({ data: [] }))
      ]);
      setApiKey(keyRes.data.catraca_api_key);
      setGymId(keyRes.data.gym_id);
      setLogs(logsRes.data);
    } catch (error) {
      console.error(error);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); registerPushSubscription(); }, [fetchData]);

  const generateKey = async () => {
    if (apiKey && !window.confirm('Gerar uma nova chave irá invalidar a chave atual. Continuar?')) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/gyms/catraca-key/generate`, {});
      setApiKey(res.data.catraca_api_key);
      toast.success('Nova chave API gerada!');
    } catch (error) {
      toast.error('Erro ao gerar chave');
    } finally { setGenerating(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiado!');
  };

  const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Integração Catraca
        </h1>
        <p className="text-slate-600 mt-1">Configure a API para check-in automático via catraca</p>
      </div>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Key className="w-5 h-5 text-orange-500" /> Chave API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {apiKey ? (
            <div className="flex items-center gap-3">
              <code data-testid="catraca-api-key" className="flex-1 bg-slate-900 text-emerald-400 px-4 py-3 rounded-xl font-mono text-sm break-all">{apiKey}</code>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(apiKey, 'key')} data-testid="copy-api-key-btn">
                {copied === 'key' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
              Nenhuma chave API gerada. Gere uma chave para integrar sua catraca.
            </div>
          )}
          <Button onClick={generateKey} disabled={generating} data-testid="generate-api-key-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
            <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {apiKey ? 'Regenerar Chave' : 'Gerar Chave API'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <FileText className="w-5 h-5 text-blue-600" /> Documentação da API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="bg-slate-50 rounded-xl p-5 space-y-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Endpoint</p>
              <div className="flex items-center gap-2">
                <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded">POST</span>
                <code className="text-sm text-slate-900 font-mono">{apiUrl}/api/checkins/catraca</code>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyToClipboard(`${apiUrl}/api/checkins/catraca`, 'url')}>
                  {copied === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Autenticação</p>
              <p className="text-sm text-slate-700">Header <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">X-Catraca-Key</code> com a chave API gerada acima.</p>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Body (JSON)</p>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-sm font-mono overflow-x-auto">{`{
  "access_code": "ABC12345"
}`}</pre>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Respostas</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">200</span>
                  <span className="text-slate-600">Check-in realizado com sucesso</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">200</span>
                  <span className="text-slate-600">Já fez check-in hoje (already_checked_in: true)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">401</span>
                  <span className="text-slate-600">Chave API inválida</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">403</span>
                  <span className="text-slate-600">Aluno sem plano / Limite de check-ins extras</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">404</span>
                  <span className="text-slate-600">Aluno não encontrado</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Terminal className="w-3.5 h-3.5" /> Exemplo com cURL
            </p>
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs font-mono overflow-x-auto whitespace-pre-wrap">{`curl -X POST "${apiUrl}/api/checkins/catraca" \\
  -H "Content-Type: application/json" \\
  -H "X-Catraca-Key: ${apiKey || 'SUA_CHAVE_API'}" \\
  -d '{"access_code": "ABC12345"}'`}</pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-7 w-7 p-0 text-slate-400 hover:text-white"
                onClick={() => copyToClipboard(`curl -X POST "${apiUrl}/api/checkins/catraca" -H "Content-Type: application/json" -H "X-Catraca-Key: ${apiKey || 'SUA_CHAVE_API'}" -d '{"access_code": "ABC12345"}'`, 'curl')}
              >
                {copied === 'curl' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Shield className="w-5 h-5 text-emerald-500" /> Logs Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Terminal className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>Nenhum check-in via catraca registrado ainda.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {logs.map((log) => (
                <div key={log.checkin_id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.is_extra_checkin ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                      <Check className={`w-4 h-4 ${log.is_extra_checkin ? 'text-amber-500' : 'text-emerald-500'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{log.student_name}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(log.timestamp).toLocaleString('pt-BR')}
                        {log.is_extra_checkin && <span className="ml-2 text-amber-600 font-medium">Extra</span>}
                      </p>
                    </div>
                  </div>
                  <code className="text-xs text-slate-400 font-mono">{log.checkin_id?.slice(-8)}</code>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CatracaAPIPage;
